import React, { useEffect, useState, useRef, useCallback } from "react";

const top10Cryptos = [
  { id: "dogecoin", symbol: "DOGE", name: "Dogecoin" },
  { id: "bitcoin", symbol: "BTC", name: "Bitcoin" },
  { id: "ethereum", symbol: "ETH", name: "Ethereum" },
  { id: "solana", symbol: "SOL", name: "Solana" },
  { id: "the-open-network", symbol: "TON", name: "Toncoin" },
  { id: "cardano", symbol: "ADA", name: "Cardano" },
  { id: "midnight-3", symbol: "NIGHT", name: "Midnight" },
  { id: "binancecoin", symbol: "BNB", name: "BNB" },
  { id: "tron", symbol: "TRX", name: "TRON" },
  { id: "ripple", symbol: "XRP", name: "XRP" },
  { id: "usd-coin", symbol: "USDC", name: "USD Coin" },
  { id: "chainlink", symbol: "LINK", name: "Chainlink" },
  { id: "hyperliquid", symbol: "HYPE", name: "Hyperliquid" },
  { id: "stellar", symbol: "XLM", name: "Stellar" },
  { id: "sui", symbol: "SUI", name: "Sui" },
  { id: "bitcoin-cash", symbol: "BCH", name: "Bitcoin Cash" },
  { id: "hedera-hashgraph", symbol: "HBAR", name: "Hedera" },
  { id: "ethena-usde", symbol: "USDe", name: "Ethena USDe" },
  { id: "avalanche-2", symbol: "AVAX", name: "Avalanche" },
  { id: "litecoin", symbol: "LTC", name: "Litecoin" },
];

const top3Metals = [
  { id: "pax-gold", symbol: "XAU", name: "Gold" },
  { id: "ishares-silver-trust-ondo-tokenized-stock", symbol: "XAG", name: "Silver" },
  { id: "diamond-standard-carat", symbol: "XDI", name: "Diamonds" },
]

const top10Stocks = [
  { id: "microsoft-ondo-tokenized-stock", symbol: "MSFT", name: "Microsoft" },
  { id: "apple-ondo-tokenized-stock", symbol: "AAPL", name: "Apple" },
  { id: "nvidia-ondo-tokenized-stock", symbol: "NVDA", name: "NVIDIA" },
  { id: "alphabet-class-a-ondo-tokenized-stock", symbol: "GOOGL", name: "Alphabet" },
  { id: "amazon-ondo-tokenized-stock", symbol: "AMZN", name: "Amazon" },
  { id: "meta-platforms-ondo-tokenized-stock", symbol: "META", name: "Meta" },
  { id: "tesla-ondo-tokenized-stock", symbol: "TSLA", name: "Tesla" },
  { id: "jpmorgan-chase-ondo-tokenized-stock", symbol: "JPM", name: "JPMorgan Chase" },
  { id: "broadcom-ondo-tokenized-stock", symbol: "AVGO", name: "Broadcom" },
  { id: "visa-ondo-tokenized-stock", symbol: "V", name: "Visa" },
  { id: "spacex-ondo-tokenized-stock", symbol: "SPCX", name: "SpaceX" },
]



export default function Top10Crypto({ onOpenModal }) {
  const [cryptoData, setCryptoData] = useState([]);
  const [metalData, setMetalData] = useState([]);
  const [stockData, setStockData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const scrollContainerRef = useRef(null);
  const scrollRowRef = useRef(null);
  const dragStartX = useRef(0);
  const scrollPositionAtDragStart = useRef(0);
  const isDraggingRef = useRef(false);
  const animationFrameRef = useRef(null);
  const hasDraggedRef = useRef(false);
  const keyboardAnimationFrameRef = useRef(null);
  const currentScrollPositionRef = useRef(0);

  // Helper function to check if cached data is still valid (less than 1 hour old)
  const isCacheValid = (timestamp) => {
    if (!timestamp) return false;
    const oneHour = 60 * 60 * 1000; // 1 hour in milliseconds
    const cacheAge = Date.now() - timestamp;
    return cacheAge < oneHour;
  };

  // Load cached data from localStorage on mount
  useEffect(() => {
    try {
      const cachedCrypto = localStorage.getItem('crypto-ticker-crypto');
      const cachedMetal = localStorage.getItem('crypto-ticker-metal');
      const cachedStock = localStorage.getItem('crypto-ticker-stock');
      const cachedCryptoTimestamp = localStorage.getItem('crypto-ticker-crypto-timestamp');
      const cachedMetalTimestamp = localStorage.getItem('crypto-ticker-metal-timestamp');
      const cachedStockTimestamp = localStorage.getItem('crypto-ticker-stock-timestamp');

      if (cachedCrypto && isCacheValid(parseInt(cachedCryptoTimestamp))) {
        const data = JSON.parse(cachedCrypto);
        setCryptoData(data);
      }
      if (cachedMetal && isCacheValid(parseInt(cachedMetalTimestamp))) {
        const data = JSON.parse(cachedMetal);
        setMetalData(data);
      }
      if (cachedStock && isCacheValid(parseInt(cachedStockTimestamp))) {
        const data = JSON.parse(cachedStock);
        setStockData(data);
        setLoading(false); // If we have stock data, we likely have all data
      }
    } catch (error) {
      console.error("Error loading cached data:", error);
    }
  }, []);

  // Helper function to fetch with retry logic
  const fetchWithRetry = async (url, maxRetries = 3) => {
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        const response = await fetch(url);
        
        // Handle 429 (Too Many Requests) - wait 5 seconds before retry
        if (response.status === 429) {
          const retryAfter = response.headers.get('Retry-After');
          const waitTime = retryAfter 
            ? parseInt(retryAfter) * 1000 
            : 5000; // 5 seconds
          
          console.warn(`Rate limited (429). Waiting ${waitTime}ms before retry ${attempt + 1}/${maxRetries}`);
          await new Promise(resolve => setTimeout(resolve, waitTime));
          continue;
        }
        
        // Handle other errors
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        return data;
      } catch (error) {
        if (attempt === maxRetries - 1) {
          throw error; // Re-throw on final attempt
        }
        // Wait 5 seconds before retry for other errors
        await new Promise(resolve => setTimeout(resolve, 5000));
      }
    }
  };

  useEffect(() => {
    const fetchCryptoPrices = async () => {
      try {
        const ids = top10Cryptos.map((crypto) => crypto.id).join(",");
        const data = await fetchWithRetry(
          `https://api.coingecko.com/api/v3/simple/price?ids=${ids}&vs_currencies=usd&include_24hr_change=true`
        );

        const formattedData = top10Cryptos.map((crypto) => ({
          ...crypto,
          price: data[crypto.id]?.usd || 0,
          change24h: data[crypto.id]?.usd_24h_change || 0,
        }));

        setCryptoData(formattedData);
        // Save to localStorage with timestamp
        localStorage.setItem('crypto-ticker-crypto', JSON.stringify(formattedData));
        localStorage.setItem('crypto-ticker-crypto-timestamp', Date.now().toString());
      } catch (error) {
        console.error("Error fetching crypto prices:", error);
      }
    };

    const fetchMetalPrices = async () => {
      try {
        const ids = top3Metals.map((metal) => metal.id).join(",");
        const data = await fetchWithRetry(
          `https://api.coingecko.com/api/v3/simple/price?ids=${ids}&vs_currencies=usd&include_24hr_change=true`
        );

        const formattedData = top3Metals.map((metal) => ({
          ...metal,
          price: data[metal.id]?.usd || 0,
          change24h: data[metal.id]?.usd_24h_change || 0,
        }));

        setMetalData(formattedData);
        // Save to localStorage with timestamp
        localStorage.setItem('crypto-ticker-metal', JSON.stringify(formattedData));
        localStorage.setItem('crypto-ticker-metal-timestamp', Date.now().toString());
      } catch (error) {
        console.error("Error fetching metal prices:", error);
      }
    };

    const fetchStockPrices = async () => {
      try {
        const ids = top10Stocks.map((stock) => stock.id).join(",");
        const data = await fetchWithRetry(
          `https://api.coingecko.com/api/v3/simple/price?ids=${ids}&vs_currencies=usd&include_24hr_change=true`
        );

        // Price overrides for pre-IPO / not-yet-trading tokens
        const PRICE_OVERRIDES = {
          "spacex-ondo-tokenized-stock": 135.00,
        };

        const formattedData = top10Stocks.map((stock) => ({
          ...stock,
          price: data[stock.id]?.usd || PRICE_OVERRIDES[stock.id] || 0,
          change24h: data[stock.id]?.usd_24h_change || 0,
        }));

        setStockData(formattedData);
        setLoading(false);
        // Save to localStorage with timestamp
        localStorage.setItem('crypto-ticker-stock', JSON.stringify(formattedData));
        localStorage.setItem('crypto-ticker-stock-timestamp', Date.now().toString());
      } catch (error) {
        console.error("Error fetching stock prices:", error);
        setLoading(false);
      }
    };

    const fetchAll = async () => {
      // Add 1 second delays between requests to avoid rate limiting
      await fetchCryptoPrices();
      await new Promise(resolve => setTimeout(resolve, 1000)); // 1 second delay
      await fetchMetalPrices();
      await new Promise(resolve => setTimeout(resolve, 1000)); // 1 second delay
      await fetchStockPrices();
    };

    // Check if we have valid cached data (less than 1 hour old)
    const cachedStockTimestamp = localStorage.getItem('crypto-ticker-stock-timestamp');
    const hasValidCache = cachedStockTimestamp && isCacheValid(parseInt(cachedStockTimestamp));
    
    if (!hasValidCache) {
      // No valid cache, fetch immediately
      fetchAll();
    } else {
      // Valid cache exists, fetch fresh data in background
      fetchAll();
    }
    
    // Increase interval to 5 minutes to reduce rate limiting issues
    const interval = setInterval(fetchAll, 300000);

    return () => clearInterval(interval);
  }, []);

  // JavaScript-controlled scrolling animation
  useEffect(() => {
    if (!scrollRowRef.current) return;

    const scrollSpeed = 0.5; // pixels per frame (adjust for speed)
    let lastTime = performance.now();

    const animate = (currentTime) => {
      if (!isDragging && !isHovered && scrollRowRef.current) {
        const deltaTime = currentTime - lastTime;
        lastTime = currentTime;
        
        const rowWidth = scrollRowRef.current.scrollWidth;
        const halfWidth = rowWidth / 2; // Scroll to -50% for seamless loop
        
        setScrollPosition((prev) => {
          let newPos = prev - (scrollSpeed * (deltaTime / 16)); // Normalize to 60fps
          
          // Wrap around for seamless loop
          if (newPos <= -halfWidth) {
            newPos = 0;
          }
          
          return newPos;
        });
      }
      
      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isDragging, isHovered]);

  // Drag handlers - unified for mouse and touch
  const handleMove = useCallback((clientX) => {
    if (!isDraggingRef.current || !scrollRowRef.current) return;
    
    const deltaX = clientX - dragStartX.current;
    
    // Mark as dragged if movement is significant (more than 5px)
    if (Math.abs(deltaX) > 5) {
      hasDraggedRef.current = true;
    }
    
    const newPosition = scrollPositionAtDragStart.current + deltaX;
    
    // Get the row width to calculate bounds
    const rowWidth = scrollRowRef.current.scrollWidth;
    const halfWidth = rowWidth / 2;
    
    // Clamp the position to prevent going too far
    const clampedPos = Math.max(-halfWidth, Math.min(0, newPosition));
    
    setScrollPosition(clampedPos);
  }, []);

  const handleMouseMove = useCallback((e) => {
    handleMove(e.clientX);
  }, [handleMove]);

  const handleTouchMove = useCallback((e) => {
    if (e.touches.length > 0) {
      e.preventDefault(); // Prevent scrolling
      handleMove(e.touches[0].clientX);
    }
  }, [handleMove]);

  const handleEnd = useCallback(() => {
    setIsDragging(false);
    isDraggingRef.current = false;
    
    // Reset drag flag after a short delay to allow click event
    setTimeout(() => {
      hasDraggedRef.current = false;
    }, 100);
  }, []);

  const handleMouseUp = useCallback(() => {
    handleEnd();
  }, [handleEnd]);

  const handleTouchEnd = useCallback(() => {
    handleEnd();
  }, [handleEnd]);

  // Handle drag events - must be before early return
  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.addEventListener("touchmove", handleTouchMove, { passive: false });
      document.addEventListener("touchend", handleTouchEnd);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.removeEventListener("touchmove", handleTouchMove);
        document.removeEventListener("touchend", handleTouchEnd);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  // Update ref whenever scrollPosition changes
  useEffect(() => {
    currentScrollPositionRef.current = scrollPosition;
  }, [scrollPosition]);

  // Keyboard shortcuts for dragging: Cmd/Ctrl + Left/Right arrows
  useEffect(() => {
    let startPosition = 0;
    let targetPosition = 0;
    let startTime = 0;
    const animationDuration = 300; // milliseconds

    const animateScroll = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / animationDuration, 1);
      
      // Easing function for smooth animation (ease-out)
      const easeOut = 1 - Math.pow(1 - progress, 3);
      
      const currentPos = startPosition + (targetPosition - startPosition) * easeOut;
      setScrollPosition(currentPos);
      currentScrollPositionRef.current = currentPos;
      
      if (progress < 1) {
        keyboardAnimationFrameRef.current = requestAnimationFrame(animateScroll);
      } else {
        keyboardAnimationFrameRef.current = null;
        startTime = 0;
      }
    };

    const handleKeyDown = (e) => {
      // Check if Cmd (Mac) or Ctrl (Windows/Linux) is pressed
      const isModifierPressed = e.metaKey || e.ctrlKey;
      
      if (!isModifierPressed) return;
      
      if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
        e.preventDefault(); // Prevent browser navigation
        
        if (!scrollRowRef.current) return;
        
        // Cancel any ongoing animation
        if (keyboardAnimationFrameRef.current) {
          cancelAnimationFrame(keyboardAnimationFrameRef.current);
          keyboardAnimationFrameRef.current = null;
        }
        
        const scrollAmount = 20; // Pixels to scroll per keypress
        const rowWidth = scrollRowRef.current.scrollWidth;
        const halfWidth = rowWidth / 2;
        
        // Get current position from ref
        const currentPos = currentScrollPositionRef.current;
        let newPos;
        
        if (e.key === "ArrowLeft") {
          // Scroll left (decrease position)
          newPos = currentPos - scrollAmount;
        } else {
          // Scroll right (increase position)
          newPos = currentPos + scrollAmount;
        }
        
        // Clamp the position to prevent going too far
        const clampedPos = Math.max(-halfWidth, Math.min(0, newPos));
        
        // Start animation
        startPosition = currentPos;
        targetPosition = clampedPos;
        startTime = 0;
        keyboardAnimationFrameRef.current = requestAnimationFrame(animateScroll);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      if (keyboardAnimationFrameRef.current) {
        cancelAnimationFrame(keyboardAnimationFrameRef.current);
      }
    };
  }, []);

  const formatPrice = (price) => {
    return `$${price.toLocaleString(undefined, {
      maximumFractionDigits: 3,
      minimumFractionDigits: 0,
    })}`;
  };

  // Render skeleton item
  const renderSkeletonItem = (index) => (
    <div key={`skeleton-${index}`} className="crypto-item crypto-item-skeleton">
      <span className="crypto-symbol skeleton-text skeleton-symbol"></span>
      <span className="crypto-price skeleton-text skeleton-price"></span>
      <span className="crypto-change skeleton-text skeleton-change"></span>
    </div>
  );

  // Drag handlers
  const handleStart = (clientX) => {
    setIsDragging(true);
    isDraggingRef.current = true;
    hasDraggedRef.current = false;
    dragStartX.current = clientX;
    scrollPositionAtDragStart.current = scrollPosition;
  };

  const handleMouseDown = (e) => {
    handleStart(e.clientX);
    e.preventDefault();
  };

  const handleTouchStart = (e) => {
    if (e.touches.length > 0) {
      handleStart(e.touches[0].clientX);
    }
  };

  if (loading) {
    // Estimate number of items (crypto + metals + stocks)
    const totalItems = top10Cryptos.length + top3Metals.length + top10Stocks.length;
    const skeletonItems = Array.from({ length: totalItems * 3 }, (_, i) => renderSkeletonItem(i));
    
    return (
      <div className="top10-crypto">
        <div className="crypto-gradient-wrapper">
          <div
            ref={scrollContainerRef}
            className="crypto-scroll-container"
            style={{ 
              cursor: isDragging ? "grabbing" : "grab",
              userSelect: isDragging ? "none" : "auto"
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => {
              setIsHovered(false);
              if (!isDragging) {
                setIsDragging(false);
                isDraggingRef.current = false;
              }
            }}
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
          >
            <div 
              ref={scrollRowRef}
              className="crypto-row"
              style={{ 
                transform: `translateX(${scrollPosition}px)`
              }}
            >
              {skeletonItems}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Combine all data in order: crypto -> metals -> stocks
  const allData = [...cryptoData, ...metalData, ...stockData];

  // Handle item click to open modal (only if not dragging)
  const handleItemClick = (item, e) => {
    // Don't open modal if we just finished dragging
    if (hasDraggedRef.current || isDraggingRef.current) {
      e.stopPropagation();
      return;
    }
    if (onOpenModal) {
      onOpenModal(item);
    }
  };

  // Render a single item
  const renderItem = (item, duplicateIndex) => (
    <div 
      key={`${item.id}-${duplicateIndex}`} 
      className="crypto-item"
      onClick={(e) => handleItemClick(item, e)}
      style={{ cursor: "pointer" }}
    >
      <span className="crypto-symbol">{item.symbol}</span>
      <span className="crypto-price">{formatPrice(item.price)}</span>
      <span
        className={`crypto-change ${
          item.change24h >= 0 ? "positive" : "negative"
        }`}
      >
        {item.change24h >= 0 ? "+" : ""}
        {item.change24h.toFixed(2)}%
      </span>
    </div>
  );

  // Number of duplicates for seamless scrolling
  const duplicates = 5;

  return (
    <div className="top10-crypto">
      <div className="crypto-gradient-wrapper">
      <div
          ref={scrollContainerRef}
        className="crypto-scroll-container"
          style={{ 
            cursor: isDragging ? "grabbing" : "grab",
            userSelect: isDragging ? "none" : "auto"
          }}
        onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => {
            setIsHovered(false);
            if (!isDragging) {
              setIsDragging(false);
              isDraggingRef.current = false;
            }
          }}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        >
          <div 
            ref={scrollRowRef}
            className="crypto-row"
            style={{ 
              transform: `translateX(${scrollPosition}px)`
            }}
          >
            {Array.from({ length: duplicates }, (_, duplicateIndex) =>
              allData.map((item) => renderItem(item, duplicateIndex))
            ).flat()}
            </div>
        </div>
      </div>
    </div>
  );
}
